// HoleSchematic
//
// Per-hole layout diagram drawn from our own course_coordinates points (tee at
// bottom, green at top, auto-rotated). No map basemap — pure SVG from our data,
// so it's free, cache-forever, and works offline. Used on the course detail
// screen and as the no-token fallback for the on-course GPS view.

import { useMemo } from 'react'
import { buildHoleGeometry } from '../lib/holeSchematic.js'

const COLORS = {
  bg: '#14361f',        // turf
  fairway: '#1f5130',
  green: '#39FF14',     // brand electric green, used sparingly = the putting green
  greenStroke: '#2bbf10',
  bunker: '#d9c48a',
  water: '#3b82c4',
  trees: '#0e2a18',
  line: 'rgba(255,255,255,0.55)',
  tee: '#D4B97A',       // champagne gold
  text: 'rgba(255,255,255,0.9)',
}

function hazardRadius(kind) {
  if (kind === 'water') return 7
  if (kind === 'trees') return 6
  return 5 // bunker
}

export default function HoleSchematic({ coords, hole, par, className, style }) {
  const geo = useMemo(() => buildHoleGeometry(coords, hole), [coords, hole])

  if (!geo) {
    return (
      <div className={className} style={{ padding: 24, textAlign: 'center', ...style }}>
        <p className="text-muted text-sm">No layout data for this hole yet.</p>
      </div>
    )
  }

  const { viewBox, tee, green, greenCenter, centerline, hazards, markers, yards } = geo
  const path = centerline.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')

  // When the caller fixes a height (e.g. the compact course-detail row), lay out
  // as a flex column so the SVG fills the space left above the label instead of
  // overflowing it. Without a fixed height, keep the original intrinsic sizing.
  const hasFixedH = style && style.height != null

  return (
    <div className={className} style={{ ...(hasFixedH ? { display: 'flex', flexDirection: 'column' } : {}), ...style }}>
      <svg
        viewBox={`0 0 ${viewBox.w.toFixed(1)} ${viewBox.h.toFixed(1)}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Hole ${geo.hole} layout`}
        style={{ width: '100%', display: 'block', background: COLORS.bg, borderRadius: 12, ...(hasFixedH ? { flex: 1, minHeight: 0 } : { height: '100%' }) }}
      >
        {/* centerline tee → (dogleg) → green */}
        {centerline.length >= 2 && (
          <path d={path} fill="none" stroke={COLORS.line} strokeWidth="2.5"
            strokeDasharray="5 5" strokeLinecap="round" strokeLinejoin="round" />
        )}

        {/* distance markers (100/150/200) */}
        {markers.map((m, i) => (
          <g key={`m${i}`}>
            <circle cx={m.x} cy={m.y} r="2" fill={COLORS.line} />
            <text x={m.x + 4} y={m.y + 3} fontSize="7" fill={COLORS.text}>{m.label}</text>
          </g>
        ))}

        {/* hazards */}
        {hazards.map((hz, i) => (
          <circle
            key={`h${i}`}
            cx={hz.x} cy={hz.y} r={hazardRadius(hz.kind)}
            fill={COLORS[hz.kind] || COLORS.bunker}
            stroke="rgba(0,0,0,0.25)" strokeWidth="0.75"
          />
        ))}

        {/* green */}
        {green && (
          <g transform={`rotate(${green.rotateDeg} ${green.cx} ${green.cy})`}>
            <ellipse cx={green.cx} cy={green.cy} rx={green.rx} ry={green.ry}
              fill={COLORS.green} stroke={COLORS.greenStroke} strokeWidth="1.5" />
          </g>
        )}
        {greenCenter && <circle cx={greenCenter.x} cy={greenCenter.y} r="1.6" fill="#0A0F16" />}

        {/* tee */}
        {tee && (
          <rect x={tee.x - 4} y={tee.y - 3} width="8" height="6" rx="1.5"
            fill={COLORS.tee} stroke="rgba(0,0,0,0.3)" strokeWidth="0.5" />
        )}
      </svg>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, padding: '0 4px' }}>
        <span style={{ fontWeight: 600 }}>Hole {geo.hole}{par ? ` · Par ${par}` : ''}</span>
        {yards != null && <span className="text-muted text-sm">{yards} yds</span>}
      </div>
    </div>
  )
}
